/**
 * The words behind "edit on the page", as plain functions.
 *
 * A heading on the storefront can carry `*emphasis*` - the word between the
 * asterisks is drawn in the theme's display face (see `HeadingText`). Those
 * markers are a storage detail, not something a merchant typing into their own
 * banner should have to step around, so while a heading is being edited on the
 * page it shows as the words alone. That raises the question this module
 * answers: when the merchant presses Enter, where do the asterisks go back?
 *
 * The answer is "around the same words, if the words are still there". A
 * merchant who changes `Shop the *Diwali* edit` to `Shop the Diwali sale`
 * keeps the emphasis on "Diwali"; one who rewrites the whole line loses it,
 * which is the honest outcome - the emphasised word no longer exists, and the
 * side panel is where they can mark a new one.
 */

/** The words of a heading, with the emphasis markers taken out. */
export function stripEmphasis(value: string): string {
  return value.replace(/\*/g, '');
}

/**
 * The next stored value for a heading that was edited as plain text.
 *
 * `original` is the stored value (markers included) and `typed` what the
 * merchant left in the box. Returns `original` itself when the words did not
 * change, so a click in and out of a heading is not an edit - and not a step
 * in the undo history either.
 */
export function restoreEmphasis(original: string, typed: string): string {
  if (typed === stripEmphasis(original)) return original;
  // Something the merchant typed with asterisks of their own is theirs to
  // keep: they know the syntax, and second-guessing it would eat their mark.
  if (typed.includes('*')) return typed;

  const emphasised = original
    .split('*')
    .filter((_, index) => index % 2 === 1)
    .filter((part) => part.trim().length > 0);
  if (emphasised.length === 0) return typed;

  let next = typed;
  let from = 0;
  for (const words of emphasised) {
    // Searched from after the last one placed, so two emphasised words keep
    // their order and a word that appears twice is marked where it was.
    const at = next.indexOf(words, from);
    if (at === -1) continue;
    next = `${next.slice(0, at)}*${words}*${next.slice(at + words.length)}`;
    from = at + words.length + 2;
  }
  return next;
}

/**
 * Text read back out of an editable element, as the merchant sees it.
 *
 * `innerText` is what knows that a `<br>` a browser inserted for Shift+Enter
 * is a line break; jsdom and very old engines lack it, and `textContent` is
 * the next best reading. A trailing newline is the browser's own artefact of
 * an empty last line, never something the merchant meant.
 */
export function readEditableText(element: HTMLElement): string {
  const raw = typeof element.innerText === 'string' ? element.innerText : (element.textContent ?? '');
  return raw.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
}

/** One line fields lose any line break a paste brought in. */
export function singleLine(value: string): string {
  return value.replace(/\s*\n+\s*/g, ' ');
}
